import { btnS } from "../../../lib/styles.js";
import {
  C, font, mono, PageWrap, SectionHeader, StatPill, StatusPill, Chip, EmptyState, JobCard,
  groupByShoot, ShootGroup,
} from "../components/ui.jsx";
import { getRecipe, getControlSet } from "../lib/recipes.js";
import { SUBJECT_TYPE_LABELS } from "../lib/mockData.js";

export default function HistoryView({ jobs, onRerun }) {
  const history = jobs.filter((j) => j.status === "complete" || j.status === "failed");
  const completeCount = history.filter((j) => j.status === "complete").length;
  const failedCount = history.filter((j) => j.status === "failed").length;

  return (
    <PageWrap>
      <SectionHeader
        eyebrow="Recent Runs"
        title="History"
        subtitle="Completed and failed processing runs"
        right={
          <>
            <StatPill value={completeCount} label="Complete" color={C.green} />
            <StatPill value={failedCount} label="Failed" color={C.danger} />
          </>
        }
      />

      {history.length === 0 ? (
        <EmptyState title="No runs yet" body="Finished jobs — successful or failed — show up here with their outputs and logs." />
      ) : (
        groupByShoot(history).map((g) => (
          <ShootGroup key={g.shoot.id} shoot={g.shoot} count={g.jobs.length}>
            {g.jobs.map((job) => <HistoryRow key={job.id} job={job} onRerun={onRerun} />)}
          </ShootGroup>
        ))
      )}
    </PageWrap>
  );
}

function HistoryRow({ job, onRerun }) {
  const recipe = getRecipe(job.recipeId);
  const control = getControlSet(job.controlSet);
  const failed = job.status === "failed";

  return (
    <JobCard status={job.status}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 400, color: C.text, marginBottom: 5 }}>{job.label}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Chip color={C.amber}>{recipe?.name || "—"}</Chip>
            <span style={{ fontSize: 11, color: C.muted }}>
              {SUBJECT_TYPE_LABELS[job.subjectType] || "Object"} · {control?.label || "—"} · {job.photoCount} photos
            </span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
          <StatusPill status={job.status} />
          <div style={{ display: "flex", gap: 6 }}>
            {!failed && <button style={{ ...btnS, padding: "4px 10px", fontSize: 11 }}>Logs</button>}
            <button style={{ ...btnS, padding: "4px 10px", fontSize: 11 }} onClick={() => onRerun?.(job)}>
              Re-run
            </button>
          </div>
        </div>
      </div>

      {failed ? (
        <div
          style={{
            marginTop: 10,
            padding: "10px 12px",
            background: `${C.danger}0d`,
            border: `1px solid ${C.danger}33`,
            borderRadius: 6,
            fontSize: 12,
            color: C.text,
            lineHeight: 1.4,
          }}
        >
          <span style={{ color: C.danger, fontWeight: 500 }}>Failed at {job.failed.failedStep}:</span>{" "}
          {job.failed.error}
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{job.failed.failedAt}</div>
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {(recipe?.variants || []).map((v) => (
              <Chip key={v.name} color={C.green}>
                {v.name} · {v.formats.join(" / ")}
              </Chip>
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            {job.complete.completedAt} · {job.complete.duration}
          </div>
        </div>
      )}
    </JobCard>
  );
}
